import { saveAs } from 'file-saver';
import nocodeClient from '../lib/nocode';
import { logger } from './logging';

const TABLES = {
  members: 'members_bpa_5e7gf',
  notes: 'notes_bpa_5e7gf',
  coaches: 'coaches_bpa_5e7gf',
  memberGoals: 'member_goals_bpa_5e7gf',
  memberMiniGoals: 'member_mini_goals_bpa_5e7gf',
  appSettings: 'app_settings_bpa_5e7gf',
};

const showToast = (message, type = 'info', duration = 5000) => {
  const toast = document.createElement('div');
  const colors = {
    info: 'bg-primary-600',
    success: 'bg-green-600',
    error: 'bg-red-600',
  };
  toast.className = `fixed top-4 right-4 ${colors[type]} text-white px-4 py-2 rounded-lg shadow-lg z-50 transition-opacity duration-300 opacity-0`;
  toast.textContent = message;
  document.body.appendChild(toast);

  // Fade in
  setTimeout(() => toast.style.opacity = '1', 10);

  // Fade out
  setTimeout(() => {
    toast.style.opacity = '0';
    setTimeout(() => {
      if (document.body.contains(toast)) {
        document.body.removeChild(toast);
      }
    }, 300);
  }, duration);
  return toast;
};

export const exportDataToJSON = async () => {
  logger.info('BACKUP', 'Starting comprehensive backup...');
  const toast = showToast('Preparing comprehensive backup...', 'info', 60000);
  try {
    toast.textContent = 'Fetching all data from database...';
    const backupData = {
      meta: {
        backup_date: new Date().toISOString(),
        backup_version: '3.0-json',
      },
    };

    const dataPromises = Object.entries(TABLES).map(async ([key, tableName]) => {
      const response = await nocodeClient.read(tableName);
      backupData[key] = response.data || [];
    });

    await Promise.all(dataPromises);
    toast.textContent = 'Creating backup file...';

    const backupBlob = new Blob([JSON.stringify(backupData, null, 2)], {
      type: 'application/json;charset=utf-8',
    });
    const fileName = `bpa_complete_backup_${new Date().toISOString().split('T')[0]}.json`;
    saveAs(backupBlob, fileName);
    
    logger.success('BACKUP', 'Backup created successfully!', { fileName });
    showToast('Backup created successfully!', 'success');
  } catch (error) {
    logger.error('BACKUP', 'Backup failed.', { error: error.message });
    console.error('Backup error:', error);
    showToast(`Backup failed: ${error.message}`, 'error');
  } finally {
    if (document.body.contains(toast)) {
      document.body.removeChild(toast);
    }
  }
};

export const importDataFromJSON = async (file) => {
  logger.info('RESTORE', 'Starting restore process...', { fileName: file.name });
  const toast = showToast('Starting restore process...', 'info', 120000);
  try {
    toast.textContent = 'Reading backup file...';
    const text = await file.text();
    const backupData = JSON.parse(text);

    if (!backupData.meta || !backupData.meta.backup_version || !backupData.meta.backup_version.includes('json')) {
      throw new Error('Invalid or incompatible backup file format.');
    }

    for (const [key, tableName] of Object.entries(TABLES)) {
      const backupItems = backupData[key];
      if (!backupItems) {
        console.log(`No data for ${key} in backup, skipping.`);
        continue;
      }

      logger.info('RESTORE', `Processing table: ${key}`);
      const liveResponse = await nocodeClient.read(tableName);
      const liveItems = liveResponse.data || [];
      const liveItemIds = new Set(liveItems.map(item => item.id));
      const backupItemIds = new Set(backupItems.map(item => item.id));

      const updatePromises = backupItems
        .filter(item => liveItemIds.has(item.id))
        .map(item => {
          const { id, ...payload } = item;
          return nocodeClient.update(tableName, id, payload);
        });

      if (updatePromises.length > 0) {
        toast.textContent = `Updating ${updatePromises.length} existing ${key}...`;
        await Promise.all(updatePromises);
        logger.success('RESTORE', `Updated ${updatePromises.length} records in ${key}`);
      }

      const itemsToCreate = backupItems.filter(item => !liveItemIds.has(item.id));
      if (itemsToCreate.length > 0) {
        toast.textContent = `Creating ${itemsToCreate.length} new ${key}...`;
        const createPromises = itemsToCreate.map(item => {
          const { id, ...payload } = item; // Cannot provide ID on creation
          return nocodeClient.create(tableName, payload);
        });
        await Promise.all(createPromises);
        logger.success('RESTORE', `Created ${itemsToCreate.length} records in ${key}`);
      }

      const itemsToArchive = liveItems.filter(item => !backupItemIds.has(item.id));
      const ARCHIVABLE_TABLES = ['members', 'coaches', 'memberGoals', 'memberMiniGoals'];
      if (ARCHIVABLE_TABLES.includes(key) && itemsToArchive.length > 0) {
        toast.textContent = `Archiving ${itemsToArchive.length} extra ${key}...`;
        const archivePromises = itemsToArchive.map(item => {
          return nocodeClient.update(tableName, item.id, { ...item, is_archived: true });
        });
        await Promise.all(archivePromises);
        logger.success('RESTORE', `Archived ${itemsToArchive.length} records in ${key}`);
      }
    }

    logger.success('RESTORE', 'Data restored successfully!');
    showToast('Data restored successfully! The page will now reload.', 'success');
    setTimeout(() => {
      window.location.reload();
    }, 2000);
  } catch (error) {
    logger.error('RESTORE', 'Restore failed.', { error: error.message });
    console.error('Restore error:', error);
    showToast(`Restore failed: ${error.message}`, 'error');
  } finally {
    if (document.body.contains(toast)) {
      document.body.removeChild(toast);
    }
  }
};